import * as Sentry from "@sentry/react";

// GlitchTip / Sentry error reporting. DSN is baked at build time via
// REACT_APP_SENTRY_DSN in eas-portal/.env.prod (consumed by env-cmd).
// When empty the SDK is a no-op so local/dev runs are unaffected.
const _sentryDsn = process.env.REACT_APP_SENTRY_DSN;
Sentry.init({
  dsn: _sentryDsn,
  enabled: Boolean(_sentryDsn),
  environment: process.env.REACT_APP_SENTRY_ENVIRONMENT ?? process.env.NODE_ENV,
  release: process.env.REACT_APP_CODE_VERSION,
  // Low sampling — matches fleetros setup, avoids production perf impact.
  tracesSampleRate: Number(
    process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE ?? 0.02
  ),
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0.05,
  // Drop expected 401/403 auth noise — normal redirects to login, not errors.
  beforeSend(event, hint) {
    const err = hint?.originalException;
    const code = err?.status ?? err?.statusCode;
    if (code === 401 || code === 403) return null;
    const isAuthMsg = (event.exception?.values ?? []).some((e) =>
      /\b401\b|\b403\b|Unauthorized|Forbidden/i.test(
        `${e.type ?? ""} ${e.value ?? ""}`
      )
    );
    return isAuthMsg ? null : event;
  },
});

import "font-awesome/css/font-awesome.min.css";
import React, { Suspense } from "react";
import "react-app-polyfill/ie11"; // For IE 11 support
import "react-app-polyfill/stable";
import { createRoot } from "react-dom/client";
import ReactGA from "react-ga4";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { install } from "resize-observer";
import App from "./App";
import "./assets/fonts/sofia-pro-cdnfonts/Sofia-Pro-Regular-Az.otf";
import { icons } from "./assets/icons";
import "./i18n";
import "./polyfill";
import * as serviceWorker from "./serviceWorker";
import { persistor, store } from "./stores/store";
import { Loader, loader } from "./utils/tableUtils";

if (!window.ResizeObserver) install();

ReactGA.initialize("G-6FCFCWT2FL");

React.icons = icons;
const container = document.getElementById("root");
const root = createRoot(container);
root.render(
  <Suspense fallback={<Loader />}>
    <React.StrictMode>
      <Provider store={store}>
        <PersistGate loading={loader} persistor={persistor}>
          <App />
        </PersistGate>
      </Provider>
    </React.StrictMode>
  </Suspense>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorker.unregister();
